// Libs
import React, { PureComponent } from 'react'
import moment from 'moment'
import _get from 'lodash/get'
import _size from 'lodash/size'
import _reduce from 'lodash/reduce'
import _sumBy from 'lodash/sumBy'
import _round from 'lodash/round'
import _isEmpty from 'lodash/isEmpty'

// Components
import Checkbox from '@react-mdc/checkbox'
import SamplingPointsTable from './SamplingPointsTable'
import Button from '@react-mdc/button'
import DownloadIcon from 'fc-icons/src/Download'

// Utils
import url from 'hfty-utils/url'
import unitConversion from 'hfty-utils/unitConversion'

// Constants
import {
  PROP_SAMPLING_JOB_ID,
  PROP_CREATED_DATE,
  PROP_STATUS,
  SET_GRID,
  SAMPLING,
  SHIPPING,
  RESULTS_POSTED,
  READY_TO_SAMPLE,
  PROP_COMPLETED_DATE,
  PROP_ASSIGNED_TO,
  PROP_COMPLETE,
} from 'fc-model-sampling-jobs/src/constants'
import {
  GRID_UNIT_ACRES,
  GRID_UNIT_HECTARES,
} from 'hfty-model-grid'

import { white, primary, tableBG } from 'hfty-styles/src/colors'
import type {
  SamplingPointOperation,
} from 'hfty-operator-sampling-point'


const INDENT = '15px'

const styles = {
  jobDetails: {
    margin: '30px 0 45px',
    padding: INDENT,
    background: white(),
    border: '1px solid rgb(220, 218, 218)',
  },
  heading: {
    margin: '0.5em 0 1em',
    fontSize: '1.1em',
    fontWeight: 400,
    lineHeight: '1.4em',
    letterSpacing: '-0.5px',
    textDecoration: 'underline',
  },
  dataTable: {
    fontSize: '0.875rem',
    marginTop: INDENT,
  },
  tdTitle: {
    fontWeight: 'bold',
    padding: '7px 20px 5px 0',
    whiteSpace: 'nowrap',
  },
  tdValue: {
    padding: '7px 0',
  },
  downloadIcon: {
    height: '17px',
    width: '24px',
    marginLeft: '-4px',
  },
  editButton: {
    float: 'right',
    marginTop: INDENT,
  },
  clearfix: {
    clear: 'both',
  },
  warning: {
    padding: INDENT,
    marginBottom: INDENT,
    fontSize: '.875rem',
    background: tableBG(),
    border: '1px solid rgb(220, 218, 218)',
  },
  checkboxWrapper: {
    display: 'flex',
    fontSize: '.875rem',
    alignItems: 'center',
  },
  checkbox: {
    position: 'absolute',
    margin: '0 4px 0 -11px',
  },
  label: {
    paddingLeft: '32px',
  },
}

type Props = {
  selectedJob: Object,
  jobDetail: Object,
  gridData: Object,
  grid: Array,
  samplingPoints: Array,
  filter: ?string,
  selectedSamplingPoint: ?number,
  imageryLayersToggled: boolean,
  token: string,
  area: number,
  operation: SamplingPointOperation,
  actions: {
    onEditJob: Function,
    selectJobFeature: Function,
    onHover: Function,
    onSelectFilter: Function,
    onClickImageryLayer: Function,
    cancelOperation: SamplingPointOperation,
  }
}
// Exports
export class JobDetail extends PureComponent {
  props: Props

  constructor (props: Props) {
    super(props)
    this.onEditJob = this.onEditJob.bind(this)
    this.onSelectFilter = this.onSelectFilter.bind(this)
  }

  onEditJob () {
    const { selectedJob } = this.props
    const jobFeatures = _get(selectedJob, 'grid.features', [])

    this.props.actions.onEditJob(selectedJob)

    if (jobFeatures.length === 1) {
      this.props.actions.selectJobFeature([0])
    }
  }

  onSelectFilter (filterOption) {
    const value = filterOption ? filterOption.value : filterOption
    this.props.actions.onSelectFilter(value)
  }

  getEstimatedSamples () {
    const { gridData } = this.props

    const points = _reduce(gridData, (acc, { gridPoints }) => {
      _sumBy(gridPoints, (point) => {
        if (point) {
          acc += 1
        }
      })

      return acc
    }, 0)

    return points
  }

  render () {
    const {
      imageryLayersToggled,
      selectedSamplingPoint,
      filter,
      jobDetail,
      gridData,
      grid,
      samplingPoints,
      token,
      area,
    } = this.props
    const { onHover, onClickImageryLayer } = this.props.actions
    const features = _get(jobDetail, 'grid.features', [])
    const isMultiGrid = (jobDetail[PROP_STATUS] === SET_GRID ||
      jobDetail[PROP_STATUS] === READY_TO_SAMPLE ||
      jobDetail[PROP_STATUS] === SAMPLING) && _size(grid) && features.length > 1
    const hasShippingPdf = jobDetail[PROP_STATUS] === SHIPPING || jobDetail[PROP_STATUS] === RESULTS_POSTED
    const samplesTitle = jobDetail[PROP_STATUS] === SET_GRID ||
    jobDetail[PROP_STATUS] === READY_TO_SAMPLE
      ? 'Estimated Samples:'
      : 'Samples:'
    const samplesValue = jobDetail[PROP_STATUS] === SET_GRID || jobDetail[PROP_STATUS] === READY_TO_SAMPLE
      ? gridData ? this.getEstimatedSamples() : 0
      : _size(samplingPoints)
    const recordDate = moment(jobDetail[PROP_CREATED_DATE], 'X').format('L')
    const completedInfo = jobDetail[PROP_COMPLETE]
      ? moment(jobDetail[PROP_COMPLETED_DATE], 'X').format('L')
      : 'No'
    const canEdit = jobDetail[PROP_STATUS] === SET_GRID || jobDetail[PROP_STATUS] === READY_TO_SAMPLE
    let currentFeature

    if (!isMultiGrid) {
      currentFeature = _get(jobDetail, 'grid.features[0]', {})
    }

    let gridSize = 'None'
    const gridSizeUnits = _get(jobDetail, 'grid.features[0].properties.units', GRID_UNIT_ACRES)
    if (_size(grid)) {
      const gridSizeValue = _get(currentFeature, 'properties.size', 0)
      gridSize = `${gridSizeValue} ${gridSizeUnits}`
    }
    const fieldArea = (gridSizeUnits === GRID_UNIT_ACRES)
      ? area
      : _round(unitConversion(area, GRID_UNIT_ACRES, GRID_UNIT_HECTARES))

    if (!jobDetail) { return null }

    return (
      <div>
        <div style={styles.jobDetails}>
          <h3 style={styles.heading}>Soil Sample Job Details – {jobDetail[PROP_SAMPLING_JOB_ID]}</h3>
          <table style={styles.dataTable}>
            <tbody>
              <tr>
                <td style={styles.tdTitle}>Status:</td>
                <td style={styles.tdValue}>{jobDetail[PROP_STATUS]}</td>
              </tr>
              {hasShippingPdf && <tr>
                <td style={styles.tdTitle}>Shipping PDF:</td>
                <td style={styles.tdValue}>
                  <a
                    download={'Shipping.pdf'}
                    target={'_blank'}
                    href={url({
                      path: '/hefty/shippingpdf/' + jobDetail[PROP_SAMPLING_JOB_ID],
                      excludeTrailingSlash: true,
                      token: token,
                    })}>
                    <DownloadIcon size={24} color={primary()} style={styles.downloadIcon} />
                  </a>
                </td>
              </tr>}
              <tr>
                <td style={styles.tdTitle}>Area:</td>
                <td style={styles.tdValue}>{fieldArea} {gridSizeUnits}</td>
              </tr>
              <tr>
                <td style={styles.tdTitle}>Grid Size:</td>
                <td style={styles.tdValue}>{isMultiGrid ? 'Multi-grid' : gridSize}</td>
              </tr>
              <tr>
                <td style={styles.tdTitle}>{samplesTitle}</td>
                <td style={styles.tdValue}>{samplesValue}</td>
              </tr>
              <tr>
                <td style={styles.tdTitle}>Assignee:</td>
                <td style={styles.tdValue}>{jobDetail[PROP_ASSIGNED_TO] || 'Not assigned'}</td>
              </tr>
              <tr>
                <td style={styles.tdTitle}>Record Date:</td>
                <td style={styles.tdValue}>{recordDate}</td>
              </tr>
              <tr>
                <td style={styles.tdTitle}>Completed:</td>
                <td style={styles.tdValue}>{completedInfo}</td>
              </tr>
            </tbody>
          </table>
          {canEdit && <div>
            <Button raised primary onClick={this.onEditJob} style={styles.editButton}>{'Edit'}</Button>
            <div style={styles.clearfix} />
          </div>}
        </div>
        {_isEmpty(samplingPoints) ? <p style={styles.warning}>No samples added yet</p> : (
          <div>
            <div style={styles.checkboxWrapper}>
              <Checkbox.Default
                id='Toggle Imagery Layers'
                name='toggle'
                value={'Show Imagery Layers'}
                checked={imageryLayersToggled}
                onChange={onClickImageryLayer}
                style={styles.checkbox}
                className='thin-border'
              />
              <label htmlFor='Toggle Imagery Layers' style={styles.label}>Show Imagery Layers</label>
            </div>
            <SamplingPointsTable
              jobStatus={jobDetail[PROP_STATUS]}
              samplingPoints={samplingPoints}
              filter={filter}
              onSelect={this.onSelectFilter}
              onHover={onHover}
              selectedPoint={selectedSamplingPoint}
              isEditing={this.props.operation && this.props.operation.operation === 'EDIT'}
              undoEditing={() => this.props.actions.cancelOperation(this.props.operation)}
            />
          </div>
        )}
      </div>
    )
  }
}

export default JobDetail
